import type { EconSectorCategory } from '../api/chartDataTypes';
import { SnapshotValueSelector, submitGuidance } from './SnapshotValueSelector';

// Module-level (not inline) so SnapshotValueSelector's internal useMemo/useCallback
// hooks — keyed in part on this function's identity — actually cache across renders.
const getSectorKey = (category: EconSectorCategory) => category.sector;

export const SECTOR_SUBMIT_GUIDANCE = submitGuidance('sector');

type SectorSelectorProps = {
    /** Snapshot categories from the econ_sector payload. Order is irrelevant —
     *  the selector sorts by `sector` ascending internally. */
    categories: EconSectorCategory[];
    /** Prefix for the Sector `<select>` id so multiple snapshot selectors on a
     *  page never collide. */
    idPrefix: string;
    /** Called only when the user presses Submit after picking a sector.
     *  Selecting the same sector again and re-submitting still fires so the
     *  parent can re-announce. */
    onSelectedCategoryChange: (category: EconSectorCategory) => void;
};

/**
 * Thin `SnapshotValueSelector` wrapper for the Economic Sector chart's
 * category picker — see `SnapshotValueSelector` for the shared Submit-gating
 * behavior (WCAG 2.2 3.2.2 On Input).
 */
export function SectorSelector({ categories, idPrefix, onSelectedCategoryChange }: SectorSelectorProps) {
    return (
        <SnapshotValueSelector<EconSectorCategory>
            values={categories}
            getKey={getSectorKey}
            idPrefix={idPrefix}
            fieldLabel='Sector'
            placeholder='Select sector'
            selectedAriaLabel='Sector'
            hint='Select a sector, then press Submit to display its portfolio weighting.'
            disabledSubmitAriaLabel='Submit — select a sector to continue'
            readySubmitAriaLabel={(sector) => `Submit and display weighting for ${sector}`}
            onSubmit={onSelectedCategoryChange}
        />
    );
}
